import { CSSProperties, FC, useEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";
import "./public-layout.scss";
import { Navbar } from "@components/navbar";
import { useFavicon } from "@hooks/use-favicon";
import { usePublishedAppForPublicForm } from "@contexts/live-app.context";
import ErrorPageLayout from "@components/error-page-layout";
import Error404Image from "@assets/404.svg";
import useUser from "@hooks/use-user";
import { useULAppTheme } from "@hooks/use-ul-app-theme";
import posthog from "posthog-js";
import { Loader } from "@components/loader";

const PublicLayout: FC = () => {
  const {
    data: appData,
    loading: isAppDataLoading,
    error: appError,
  } = usePublishedAppForPublicForm();
  const { user, loading: userLoading } = useUser();

  useEffect(() => {
    if (user?.sub && user?.["custom:companyId"]) {
      posthog.identify(user.sub, {
        organizationId: user["custom:companyId"] as string,
      });
    }
  }, [user]);

  const context = useMemo(() => {
    return {
      user,
    };
  }, [user]);

  // Per-app theme exposed as CSS variables so descendants can read
  // var(--ul-app-primary)/var(--ul-app-font) instead of drilling props.
  const themeStyle = useMemo(
    () =>
      ({
        "--ul-app-primary": appData?.getApp?.primaryColor,
        "--ul-app-font": appData?.getApp?.fontColor,
      }) as CSSProperties,
    [appData],
  );

  useULAppTheme({
    "--ul-app-primary": appData?.getApp?.primaryColor,
    "--ul-app-font": appData?.getApp?.fontColor,
  });

  useFavicon("./logo.png");

  if (isAppDataLoading || userLoading) {
    return <Loader />;
  }

  return (
    <main className="template" style={themeStyle}>
      <div className="content-area w-100">
        <div className="header-area">
          <Navbar basePath="/auth/signin" />
        </div>
        <div className="flex justify-center">
          {appError ? (
            <ErrorPageLayout
              title={
                (appError as Error & { cause?: { message?: string } }).cause?.message ??
                "Error Fetching App Details"
              }
              image={<img src={Error404Image} alt="error" decoding="async" loading="eager" />}
            />
          ) : (
            <Outlet context={context} />
          )}
        </div>
      </div>
    </main>
  );
};

export default PublicLayout;
